import * as React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Input } from "../../../../bits/Input/Input";
import { InfoIcon } from "../../../../icons/Info";
import { validateFeeFormat } from "../../../../../../utils/utils";
import { cn } from "../../../../../helpers";
import styles from "./FeeInputWithTooltip.module.scss";

const FEE_FORMAT_TOOLTIP =
    "Accepted formats: 25, 80x5; 15 TBD (or 80x5; $15 TBD), 120TBD (120 TBD), 25, 120x7; 90(or 120x7; $90), (Psych Fee formats: 455x1; 255 (or 45.5x1; 25.5), 655x1; 455 UDM, then 80, 165.76x2; 160.89 UDM, then 32.18, 452 UDM, then 22, 16.09), (Kap Fee formats: 200; 100; 1000X2 THEN 200, 200; 100; 1000), NOT ACCEPTED (for INN fees)";

interface FeeInputWithTooltipProps {
    value: string;
    label?: string;
    onChange: (value: string) => void;
    calcValue?: string | {} | undefined;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    skipValidation?: boolean;
}

export default function FeeInputWithTooltip({
    value,
    label,
    onChange,
    calcValue,
    required = true,
    disabled = false,
    className,
    skipValidation = false,
}: FeeInputWithTooltipProps) {
    // Helper function to get validation message
    const getFeeValidationMessage = (
        feeValue: string,
        calcValue?: string | {} | undefined
    ): string => {
        if (skipValidation) {
            return (typeof calcValue === "string" ? calcValue : "") || "";
        }
        if (!validateFeeFormat(feeValue)) {
            return "Wrong fee format";
        }
        return (typeof calcValue === "string" ? calcValue : "") || "";
    };

    const isValid = skipValidation ? true : validateFeeFormat(value);
    const validationMessage = getFeeValidationMessage(value, calcValue);
    const hasError = !isValid && value.trim() !== "";
    const getTooltipContent = () => {
        if (hasError) {
            return FEE_FORMAT_TOOLTIP;
        }
        if (validationMessage && !hasError) {
            return validationMessage;
        }
        return FEE_FORMAT_TOOLTIP;
    };

    const getTooltipColor = () => {
        if (hasError) {
            return "#f71735";
        }
        if (validationMessage && !hasError) {
            return "var(--color-primary-v2)";
        }
        return "var(--color-text-strong)";
    };

    return (
        <div className={cn(styles.container, className)}>
            <div className={styles.inputWrapper}>
                <Input
                    label={
                        label ? (
                            <div className={styles.labelContainer}>
                                {label}{" "}
                                {!skipValidation && (
                                    <OverlayTrigger
                                        placement="bottom"
                                        delay={{ show: 250, hide: 400 }}
                                        trigger="hover"
                                        overlay={(props) => (
                                            <Tooltip id={`tooltip-fees`} {...props}>
                                                {getTooltipContent()}
                                            </Tooltip>
                                        )}
                                    >
                                        <span>
                                            <InfoIcon stroke={getTooltipColor()} />
                                        </span>
                                    </OverlayTrigger>
                                )}
                            </div>
                        ) : undefined
                    }
                    inputClassName={styles.input}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    hasError={hasError}
                    className={styles.inputContainer}
                />
                {!skipValidation && !label && (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        trigger="hover"
                        overlay={(props) => (
                            <Tooltip id={`tooltip-fees`} {...props}>
                                {getTooltipContent()}
                            </Tooltip>
                        )}
                    >
                        <span className={styles.infoIcon}>
                            <InfoIcon stroke={getTooltipColor()} />
                        </span>
                    </OverlayTrigger>
                )}
            </div>
        </div>
    );
}
